import * as Sentry from '@sentry/browser'

// # NB! The config for the backend is in config/initializers/sentry.rb
// app/views/layouts/_sentry_init.slim only sets window.sentryConfig when Sentry is enabled for this environment.
type SentryConfig = {
  dsn: string
  environment: string
  user: { email?: string }
  company: { name?: string; hashid?: string }
}

const sentryConfig = (): SentryConfig | undefined => (window as any).sentryConfig

const config = sentryConfig()
if (config) {
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,

    // A reading/writing-assistant extension rejects its own RPC timeouts with a bare string, so there is no
    // stacktrace for the extension-frame check in beforeSend to see.
    ignoreErrors: [/^Non-Error promise rejection captured with value: Request timeout /],

    // Filter out errors from browser extensions
    beforeSend(event, hint) {
      const error = hint.originalException

      // Ignore errors from browser extensions
      if (
        event.exception?.values?.some((exception) =>
          exception.stacktrace?.frames?.some(
            (frame) =>
              frame.filename?.includes('chrome-extension://') ||
              frame.filename?.includes('moz-extension://') ||
              frame.filename?.includes('safari-extension://'),
          ),
        )
      ) {
        return null
      }

      // Ignore "Extension context invalidated" errors
      if (error instanceof Error && error.message?.includes('Extension context invalidated')) {
        return null
      }

      return event
    },

    // We have turned off all Performance and tracing monitoring for now.
    // integrations: [Sentry.browserTracingIntegration()],
    // Performance Monitoring
    // tracesSampleRate: 1.0, // Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    // replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })

  // Sentry keeps whatever it was last told, and Turbolinks re-runs the sentryConfig script in the head when it
  // changes, so a visit that switches company has to say so again or every later error is tagged with the company
  // you left.
  const applyIdentity = () => {
    const { user, company } = sentryConfig() ?? config

    if (user.email) Sentry.setUser({ email: user.email })

    if (company.hashid) {
      Sentry.setContext('Company', { name: company.name, hashid: company.hashid })
      Sentry.setTag('company', company.hashid)
    }
  }

  applyIdentity()
  document.addEventListener('turbolinks:load', applyIdentity)
  // Make Sentry available globally for compatibility
  ;(window as any).Sentry = Sentry
}
